import { SITE } from "@/data/site";
import { AVIS, NOTE_MOYENNE, NB_AVIS } from "@/data/avis";

// Base LocalBusiness / Locksmith JSON-LD — Service Area Business (pas d'accueil physique)
// L'url reste celle de l'entreprise sur toutes les pages (entité stable).
// withReviews : uniquement là où les avis sont visibles (accueil) — Google
// demande que le balisage d'avis corresponde au contenu affiché.
export const businessJsonLd = ({ areaServed, name, withReviews = false } = {}) => ({
  "@context": "https://schema.org",
  "@type": "Locksmith",
  "@id": `${SITE.siteUrl}#business`,
  name: name || SITE.nom,
  legalName: SITE.raisonSociale,
  description:
    "Serrurier de ville basé à Puteaux (92). Interventions d'urgence 7j/7 et travaux de sécurisation dans toute l'Île-de-France.",
  url: `${SITE.siteUrl}/`,
  telephone: SITE.telephoneHref.replace("tel:", ""),
  email: SITE.email,
  priceRange: "€€",
  image: `${SITE.siteUrl}/og-image.jpg`,
  hasMap: SITE.googleMaps,
  sameAs: [SITE.facebook, SITE.googleMaps],
  address: {
    "@type": "PostalAddress",
    addressLocality: SITE.ville,
    postalCode: SITE.codePostal,
    addressRegion: SITE.region,
    addressCountry: SITE.pays,
  },
  areaServed: areaServed || [
    { "@type": "AdministrativeArea", name: "Hauts-de-Seine" },
    { "@type": "AdministrativeArea", name: "Paris" },
    { "@type": "AdministrativeArea", name: "Yvelines" },
    { "@type": "AdministrativeArea", name: "Val-d'Oise" },
    { "@type": "AdministrativeArea", name: "Val-de-Marne" },
  ],
  openingHoursSpecification: {
    "@type": "OpeningHoursSpecification",
    dayOfWeek: ["Monday", "Tuesday", "Wednesday", "Thursday", "Friday", "Saturday", "Sunday"],
    opens: "00:00",
    closes: "23:59",
  },
  identifier: [
    { "@type": "PropertyValue", propertyID: "SIREN", value: SITE.siren.replace(/\s/g, "") },
    { "@type": "PropertyValue", propertyID: "SIRET", value: SITE.siret.replace(/\s/g, "") },
  ],
  ...(withReviews && {
    aggregateRating: {
      "@type": "AggregateRating",
      ratingValue: NOTE_MOYENNE.toFixed(1),
      reviewCount: String(NB_AVIS),
      bestRating: "5",
      worstRating: "1",
    },
    review: AVIS.map((a) => ({
      "@type": "Review",
      author: { "@type": "Person", name: a.auteur },
      reviewRating: { "@type": "Rating", ratingValue: String(a.note), bestRating: "5" },
      reviewBody: a.texte,
      publisher: { "@type": "Organization", name: a.source },
    })),
  }),
});

export const breadcrumbJsonLd = (items) => ({
  "@context": "https://schema.org",
  "@type": "BreadcrumbList",
  itemListElement: items.map((it, i) => ({
    "@type": "ListItem",
    position: i + 1,
    name: it.name,
    item: it.url,
  })),
});

// Service rattaché à l'entreprise via son @id (défini dans businessJsonLd)
export const serviceJsonLd = ({ name, description, url, prestations = [] }) => ({
  "@context": "https://schema.org",
  "@type": "Service",
  name,
  serviceType: name,
  description,
  url,
  provider: { "@id": `${SITE.siteUrl}#business` },
  areaServed: { "@type": "AdministrativeArea", name: "Île-de-France" },
  hasOfferCatalog: {
    "@type": "OfferCatalog",
    name,
    itemListElement: prestations.map((p) => ({
      "@type": "Offer",
      itemOffered: { "@type": "Service", name: p.nom },
    })),
  },
});

export const faqJsonLd = (items) => ({
  "@context": "https://schema.org",
  "@type": "FAQPage",
  mainEntity: items.map((it) => ({
    "@type": "Question",
    name: it.q,
    acceptedAnswer: {
      "@type": "Answer",
      text: it.r,
    },
  })),
});
